import React from "react";
import Dropdowns from "./Dropdowns";
import { Footer, LoadingIcon, Sidebar } from "../../../../components";
import GreenhouseMapModal from "./GreenhouseMapModal";
import ExportHarvestedExcelModal from "./ExportHarvestedExcelModal.jsx";
import RecommendationCards from "./RecommendationCards.jsx";
import useSpinachDashboardState from "./useSpinachDashboardState";
import SpinachDashboardControls from "./SpinachDashboardControls";
import SpinachDashboardStats from "./SpinachDashboardStats";
import SpinachDashboardGroups from "./SpinachDashboardGroups";

const SpinachDashboard = (props) => {
  const {
    contextHolder,
    sidebarOpen,
    setSidebarOpen,
    siteMetadata,
    setSiteMetadata,
    currentOrg,
    setCurrentOrg,
    orgData,
    setOrgData,
    orgLoading,
    setOrgLoading,
    currentSite,
    setCurrentSite,
    siteData,
    setSiteData,
    siteLoading,
    setSiteLoading,
    currentCrop,
    setCurrentCrop,
    cropData,
    setCropData,
    cropLoading,
    setCropLoading,
    greenhouseData,
    setGreenhouseData,
    setDropdownLoading,
    searchParams,
    setSearchParams,
    settingDefaults,
    setSettingDefaults,
    setBlockLabel,
    blockLabel,
    greenHouseLabel,
    setGreenHouseLabel,
    selectedGreenhouses,
    setSelectedGreenhouses,
    minLeafSizeInput,
    maxLeafSizeInput,
    setMinLeafSizeInput,
    setMaxLeafSizeInput,
    savingLeafSize,
    handleSaveLeafSize,
    minimumIdealHarvestLeafSize,
    maximumIdealHarvestLeafSize,
    actionsDisabled,
    openMapModal,
    handleSaveAllData,
    userData,
    expandedGroups,
    setExpandedGroups,
    type1Count,
    type2Count,
    type3Count,
    renderExceedList,
    renderMidStageList,
    renderEarlyMidStageList,
    handleGroupChange,
    filteredByType,
    renderGreenhouseTable,
    groupConfig,
    isLoading,
    greenHouseTableLoading,
    summaryForecastData,
    showRecommendationFollowSpinachDashboard,
    greenhouseMapVisible,
    closeMapModal,
    groupedGreenhouses,
    cardConfig,
    showForecastWeightColumn,
    weightKeyNameForTable,
    yieldKeyNameForTable,
    exportModalVisible,
    setExportModalVisible,
    excelRawData,
    allRowGraphData,
    latLong,
    gddBaseTemperature,
    fetchHarvestedData,
    createHarvestedMap,
    extractTableDataFromRawData,
    getAverageRowLevelData,
    getSortedFilteredDatesRange,
    memoizedCalcDiffRange,
    getTempSliceOrFetch,
    getForecastLeafSize,
  } = useSpinachDashboardState(props);

  const dropdownProps = {
    siteMetadata,
    setSiteMetadata,
    currentOrg,
    setCurrentOrg,
    orgData,
    setOrgData,
    orgLoading,
    setOrgLoading,
    currentSite,
    setCurrentSite,
    siteData,
    setSiteData,
    siteLoading,
    setSiteLoading,
    currentCrop,
    setCurrentCrop,
    cropData,
    setCropData,
    cropLoading,
    setCropLoading,
    greenhouseData,
    setGreenhouseData,
    setDropdownLoading,
    searchParams,
    setSearchParams,
    settingDefaults,
    setSettingDefaults,
    setBlockLabel,
    blockLabel,
    greenHouseLabel,
    setGreenHouseLabel,
    selectedGreenhouses,
    setSelectedGreenhouses,
  };

  const controlsProps = {
    minLeafSizeInput,
    maxLeafSizeInput,
    setMinLeafSizeInput,
    setMaxLeafSizeInput,
    savingLeafSize,
    handleSaveLeafSize,
    minimumIdealHarvestLeafSize,
    maximumIdealHarvestLeafSize,
    actionsDisabled,
    onShowMap: openMapModal,
    onSaveAllData: handleSaveAllData,
    userData,
    greenHouseLabel,
  };

  const statsProps = {
    expandedGroups,
    setExpandedGroups,
    type1Count,
    type2Count,
    type3Count,
    greenHouseLabel,
    renderExceedList,
    renderMidStageList,
    renderEarlyMidStageList,
  };

  const groupsProps = {
    expandedGroups,
    handleGroupChange,
    filteredByType,
    renderGreenhouseTable,
    groupConfig,
    groupedGreenhouses,
    userData,
    setExportModalVisible,
    isLoading,
    greenHouseTableLoading,
  };

  return (
    <>
      <div id="dashboard">
        <Sidebar
          active="home"
          sidebarType="SpinachGrower"
          open={sidebarOpen}
          setOpen={setSidebarOpen}
        />
        <div
          className={`flex w-full flex-col pb-14 pt-8 lg:pt-6 sm:pt-4 ${
            sidebarOpen ? "pl-[210px] lg:pl-[50px]" : "pl-[50px]"
          } transition-[padding] duration-200 ease-in-out`}
        >
          <div className="mx-auto w-full max-w-[1900px] px-8 xl:px-6 lg:px-3 md:px-2 sm:px-1.5">
            {contextHolder}

            <Dropdowns {...dropdownProps} />

            <SpinachDashboardControls {...controlsProps} />

            <SpinachDashboardStats {...statsProps} />

            {greenHouseTableLoading && !isLoading && (
              <LoadingIcon height="100px" />
            )}

            {/* Collapsible groups for each type */}
            {!isLoading && Object.keys(summaryForecastData).length > 0 ? (
              <>
                {userData?.is_superuser &&
                  showRecommendationFollowSpinachDashboard && (
                    <RecommendationCards
                      data={summaryForecastData}
                      selectedGreenhouses={selectedGreenhouses}
                      siteMetadata={siteMetadata}
                      minLeafSize={minimumIdealHarvestLeafSize}
                      maxLeafSize={maximumIdealHarvestLeafSize}
                      greenhouseLabel={greenHouseLabel}
                      blockLabel={blockLabel}
                      yieldPerMeterTitle={
                        siteMetadata?.FORECAST_GREENHOUSE_TABLE
                          ?.YIELD_PER_METER_TITLE || "Yield Per Meter"
                      }
                    />
                  )}
                <div className="flex flex-col">
                  <SpinachDashboardGroups {...groupsProps} />
                </div>
              </>
            ) : (
              <LoadingIcon
                height="200px"
                classname="absolute h-full top-0 left-0"
              />
            )}
          </div>
        </div>
        <Footer sidebarOpen={sidebarOpen} />
      </div>
      {greenhouseMapVisible && (
        <GreenhouseMapModal
          visible={greenhouseMapVisible}
          onClose={closeMapModal}
          greenhouseCoordinates={siteMetadata?.GREENHOUSE_COORDINATES}
          greenhouseMapTileUrl={siteMetadata?.GREENHOUSE_MAP_TILE_URL}
          selectedGreenhouses={selectedGreenhouses}
          typeToGreenhouseMap={groupedGreenhouses}
          greenhouseGroupConfig={groupConfig}
          cards={cardConfig}
          greenhouseLabel={greenHouseLabel}
          blockLabel={blockLabel}
        />
      )}
      <ExportHarvestedExcelModal
        visible={exportModalVisible}
        onCancel={() => setExportModalVisible(false)}
        currentOrg={currentOrg}
        currentSite={currentSite}
        currentCrop={currentCrop}
        greenhouseData={greenhouseData}
        siteMetadata={siteMetadata || {}}
        summaryForecastData={summaryForecastData}
        greenhouseLabel={greenHouseLabel}
        blockLabel={blockLabel}
        yieldTableHeaderName={yieldKeyNameForTable}
        showWeightColumn={showForecastWeightColumn}
        weightTableHeaderName={weightKeyNameForTable}
        excelRawData={excelRawData}
        allRowGraphData={allRowGraphData}
        latLong={latLong}
        gddBaseTemperature={gddBaseTemperature}
        fetchHarvestedData={fetchHarvestedData}
        createHarvestedMap={createHarvestedMap}
        extractTableDataFromRawData={extractTableDataFromRawData}
        getAverageRowLevelData={getAverageRowLevelData}
        getSortedFilteredDatesRange={getSortedFilteredDatesRange}
        memoizedCalcDiffRange={memoizedCalcDiffRange}
        getTempSliceOrFetch={getTempSliceOrFetch}
        getForecastLeafSize={getForecastLeafSize}
        minimumIdealHarvestLeafSize={minimumIdealHarvestLeafSize}
        maximumIdealHarvestLeafSize={maximumIdealHarvestLeafSize}
      />
    </>
  );
};

export default SpinachDashboard;
